import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';
import PhotoViewer from './PhotoViewer';
import { optimizeImage } from './UploadPhotos';

function ItemDetails({ embedded = false, itemId, ownerId: itemOwnerId, initialItem, onBack, backLabel = 'Back' }) {
  const { id: routeId, ownerId: routeOwnerId } = useParams();
  const id = itemId || routeId;
  const ownerId = itemOwnerId || routeOwnerId;
  const isSocial = Boolean(ownerId);
  const navigate = useNavigate();
  const location = useLocation();
  const previewItem = initialItem ? { ...initialItem, assets: initialItem.assets || (initialItem.thumbnailUrl || initialItem.mediumUrl || initialItem.fullUrl ? [{ id: `preview-${initialItem.id}`, downloadUrl: initialItem.mediumUrl || initialItem.thumbnailUrl || initialItem.fullUrl, originalFileName: '' }] : []) } : null;
  const [item, setItem] = React.useState(previewItem);
  const previewImageUrl = initialItem?.mediumUrl || initialItem?.thumbnailUrl || initialItem?.fullUrl || '';
  const [primaryImageUrl, setPrimaryImageUrl] = React.useState(previewImageUrl);
  const [error, setError] = React.useState('');
  const [viewerIndex, setViewerIndex] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [managePhotos, setManagePhotos] = React.useState(false);
  const [uploadingPhotos, setUploadingPhotos] = React.useState(false);
  const [uploadNotice, setUploadNotice] = React.useState('');
  const fileInputRef = React.useRef(null);
  const [confirmRemove, setConfirmRemove] = React.useState(null);
  const [deleteItemOpen, setDeleteItemOpen] = React.useState(false);
  const [deletingItem, setDeletingItem] = React.useState(false);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [tagLoading, setTagLoading] = React.useState(false);
  const sortedTagOptions = React.useMemo(() => [...tagOptions].sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name, undefined, { sensitivity: 'base' })), [tagOptions]);

  React.useEffect(() => {
    let active = true;
    api.get(isSocial ? `/v1/social/users/${ownerId}/items/${id}` : `/v1/items/${id}`)
      .then(response => active && setItem(response.data))
      .catch(err => active && !initialItem && setError(getApiErrorMessage(err, 'Unable to load item.')));
    return () => { active = false; };
  }, [id, initialItem, ownerId, isSocial]);

  React.useEffect(() => {
    setPrimaryImageUrl(previewImageUrl);
  }, [id, previewImageUrl]);

  React.useEffect(() => {
    const finalImageUrl = item?.assets?.[0]?.downloadUrl;
    if (!finalImageUrl || finalImageUrl === primaryImageUrl) return undefined;
    const image = new window.Image();
    image.onload = () => setPrimaryImageUrl(finalImageUrl);
    image.src = finalImageUrl;
    return () => { image.onload = null; };
  }, [item, primaryImageUrl]);

  const beginEdit = async () => {
    setTagLoading(true);
    try {
      const { data } = await api.get('/v1/tags');
      setTagOptions(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load tags.'));
    } finally {
      setTagLoading(false);
    }
    setDraft({ name: item.name || '', location: item.location || '', notes: item.notes || '', tagIds: (item.tags || []).map(tag => tag.id) });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put(`/v1/items/${id}`, { name: draft.name, location: draft.location, notes: draft.notes }),
        api.put(`/v1/items/${id}/tags`, { tagIds: draft.tagIds })
      ]);
      const { data } = await api.get(`/v1/items/${id}`);
      setItem(data);
      setEditing(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save item details.'));
    } finally {
      setSaving(false);
    }
  };

  const openManagePhotos = async () => {
    setManagePhotos(true);
  };

  const closeManagePhotos = () => {
    if (uploadingPhotos) return;
    setManagePhotos(false);
    setUploadNotice('');
  };

  const uploadPhotosToItem = async event => {
    const files = [...event.target.files];
    event.target.value = '';
    if (files.length === 0) return;
    setUploadingPhotos(true);
    setUploadNotice('');
    let uploaded = 0;
    try {
      for (const file of files) {
        const optimized = await optimizeImage(file);
        const { data: authorization } = await api.post(`/v1/items/${id}/assets`);
        const uploadResults = await Promise.all(authorization.uploads.map(upload => fetch(upload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': authorization.contentType }, body: optimized.variants[upload.variant].blob })));
        if (uploadResults.some(response => !response.ok)) throw new Error('Photo upload failed.');
        await api.post(`/v1/items/${id}/assets/${authorization.batchId}/${authorization.assetId}/complete`, {
          originalFileName: file.name,
          sourceContentType: file.type,
          sourceByteSize: file.size,
          thumbnailByteSize: optimized.variants.thumb.blob.size,
          mediumByteSize: optimized.variants.medium.blob.size,
          fullByteSize: optimized.variants.full.blob.size,
          width: optimized.width,
          height: optimized.height,
          displayOrder: item.assets.length + uploaded
        });
        uploaded += 1;
      }
      const { data } = await api.get(`/v1/items/${id}`);
      setItem(data);
      setUploadNotice(`${uploaded} photo${uploaded === 1 ? '' : 's'} added.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Some photos could not be added.'));
    } finally {
      setUploadingPhotos(false);
    }
  };

  const removePhoto = async () => {
    if (!confirmRemove) return;
    try {
      await api.delete(`/v1/items/${id}/assets/${confirmRemove.id}`);
      const { data } = await api.get(`/v1/items/${id}`);
      setItem(data);
      setConfirmRemove(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to remove photo from this item.'));
    }
  };

  const deleteItem = async () => {
    setDeletingItem(true);
    try {
      await api.delete(`/v1/items/${id}`);
      navigate('/');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete item.'));
      setDeletingItem(false);
      setDeleteItemOpen(false);
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  const backText = embedded ? backLabel : 'Back';
  const embeddedBack = embedded && <Button className="back-link detail-back-link" onClick={goBack} startIcon={<ArrowBackIcon />}>{backText}</Button>;
  if (error) return <main className={embedded ? 'item-details-panel social-detail' : 'page-shell'}>{embeddedBack}<Alert severity="error">{error}</Alert></main>;
  if (!item) return embedded ? <main className="item-details-panel social-detail">{embeddedBack}<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></main> : <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const title = item.name || 'Untitled Item';
  return (
    <main className={embedded ? 'item-details-panel social-detail' : 'page-shell'}>
      <>{embedded && <Button className="back-link detail-back-link" onClick={goBack} startIcon={<ArrowBackIcon />}>{backText}</Button>}<Box className={embedded ? 'item-details-card' : undefined}><Box sx={{ mb: 3 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}><Typography variant="h4">{title}</Typography>{!isSocial && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}><Button onClick={openManagePhotos} startIcon={<EditIcon />} variant="contained" sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Manage Photos</Button><Button onClick={beginEdit} startIcon={<EditIcon />} variant="contained" sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Edit</Button><Button onClick={() => setDeleteItemOpen(true)} startIcon={<DeleteForeverIcon />} variant="contained" sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Delete Item</Button></Box>}</Box></Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {item.assets.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 3 }}>{item.assets.map((asset, index) => <Paper key={asset.id} sx={{ position: 'relative', overflow: 'hidden' }}><Box component="img" src={index === 0 ? (primaryImageUrl || asset.downloadUrl) : asset.downloadUrl} alt={asset.originalFileName || ''} onClick={() => setViewerIndex(index)} sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', cursor: 'pointer' }} /><IconButton onClick={() => setViewerIndex(index)} aria-label="Preview photo" sx={{ position: 'absolute', right: 6, bottom: 6, color: '#222', backgroundColor: 'rgba(255,255,255,0.9)', '&:hover': { color: '#222', backgroundColor: 'rgba(255,255,255,0.9)' } }}><SearchIcon fontSize="small" /></IconButton>{!isSocial && <IconButton onClick={() => setConfirmRemove(asset)} aria-label="Remove photo" sx={{ position: 'absolute', left: 6, top: 6, color: '#222', backgroundColor: 'rgba(255,255,255,0.9)', '&:hover': { color: '#222', backgroundColor: 'rgba(255,255,255,0.9)' } }}><DeleteOutlineIcon fontSize="small" /></IconButton>}</Paper>)}</Box>}
      {(item.location || item.notes) && <Paper sx={{ p: 2 }}>{item.location && <Typography sx={{ mb: 1 }}><strong>Location:</strong> {item.location}</Typography>}{item.notes && <Typography sx={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {item.notes}</Typography>}</Paper>}
      {item.tags?.length > 0 && <Paper sx={{ p: 2, mt: 2 }}><Typography variant="h6" gutterBottom>Tags</Typography><Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{item.tags.map(tag => <Chip key={tag.id} label={tag.name} />)}</Box></Paper>}
      <Dialog open={editing} onClose={() => !saving && setEditing(false)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Edit Item</DialogTitle><DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}><TextField label="Name" value={draft?.name || ''} onChange={event => setDraft(current => ({ ...current, name: event.target.value }))} inputProps={{ maxLength: 200 }} /><TextField label="Location" value={draft?.location || ''} onChange={event => setDraft(current => ({ ...current, location: event.target.value }))} inputProps={{ maxLength: 200 }} /><TextField label="Notes" value={draft?.notes || ''} onChange={event => setDraft(current => ({ ...current, notes: event.target.value }))} inputProps={{ maxLength: 5000 }} multiline minRows={4} /><Autocomplete multiple options={sortedTagOptions} loading={tagLoading} value={sortedTagOptions.filter(tag => draft?.tagIds?.includes(tag.id))} getOptionLabel={option => option.path || option.name} isOptionEqualToValue={(option, value) => option.id === value.id} onChange={(_event, values) => setDraft(current => ({ ...current, tagIds: values.map(tag => tag.id) }))} renderTags={(values, getTagProps) => values.map((option, index) => <Chip label={option.path || option.name} {...getTagProps({ index })} key={option.id} />)} renderInput={params => <TextField {...params} label="Tags" placeholder="Add tags" />} /></DialogContent><DialogActions><Button onClick={() => setEditing(false)} disabled={saving} sx={{ color: '#222' }}>Cancel</Button><Button onClick={saveEdit} variant="contained" disabled={saving} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold' }}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions></Dialog>
      <Dialog open={managePhotos} onClose={closeManagePhotos} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Manage Photos</DialogTitle><DialogContent><Typography color="text.secondary" sx={{ mb: 2 }}>Add photos directly to this item or permanently delete photos from it.</Typography><input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={uploadPhotosToItem} /><Button onClick={() => fileInputRef.current?.click()} variant="contained" disabled={uploadingPhotos} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{uploadingPhotos ? 'Adding photos…' : 'Add Photos'}</Button>{uploadNotice && <Alert severity="success" sx={{ mt: 2 }}>{uploadNotice}</Alert>}<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Photos are optimized before upload. Deleting a photo permanently removes it from storage.</Typography></DialogContent><DialogActions><Button onClick={closeManagePhotos} disabled={uploadingPhotos} sx={{ color: '#222' }}>Close</Button></DialogActions></Dialog>
      <Dialog open={Boolean(confirmRemove)} onClose={() => setConfirmRemove(null)}><DialogTitle sx={{ fontWeight: 'bold' }}>Delete Photo?</DialogTitle><DialogContent>This permanently deletes the photo from this item and storage. The item must keep at least one photo.</DialogContent><DialogActions><Button onClick={() => setConfirmRemove(null)} sx={{ color: '#222' }}>Cancel</Button><Button onClick={removePhoto} variant="contained" sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Delete Photo</Button></DialogActions></Dialog>
      <Dialog open={deleteItemOpen} onClose={() => !deletingItem && setDeleteItemOpen(false)}><DialogTitle sx={{ fontWeight: 'bold' }}>Delete item?</DialogTitle><DialogContent>Deleting this item removes its contents and photos as well. You will need to upload the photos again if you delete it.</DialogContent><DialogActions><Button onClick={() => setDeleteItemOpen(false)} disabled={deletingItem} sx={{ color: '#222' }}>Cancel</Button><Button onClick={deleteItem} variant="contained" disabled={deletingItem} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{deletingItem ? 'Deleting…' : 'Delete Item'}</Button></DialogActions></Dialog>
      <PhotoViewer photos={item.assets} initialIndex={viewerIndex || 0} open={viewerIndex !== null} onClose={() => setViewerIndex(null)} />
      </Box></></main>
  );
}

export default ItemDetails;
